import { createTheme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark" | "system";

// Accent colors. Each has a shade for light screens and a lighter one that
// stays readable on dark screens.
export const ACCENTS = {
  forest: { label: "Forest", light: "#2e6b3f", dark: "#6fbf85" },
  ocean: { label: "Ocean", light: "#1f5f8b", dark: "#6aaee0" },
  slate: { label: "Slate", light: "#3d4a5c", dark: "#a3b3c9" },
  maple: { label: "Maple", light: "#8a3b2b", dark: "#e39580" },
  plum: { label: "Plum", light: "#6b3f7a", dark: "#c7a0d6" },
} as const;

export type AccentKey = keyof typeof ACCENTS;

const SURFACES = {
  light: {
    appBg: "#f5f6f4",
    surface: "#ffffff",
    surface2: "#fafbf9",
    border: "#e3e6e2",
    borderSoft: "#eef0ed",
    borderStrong: "#d7dcd6",
    text: "#1d2420",
    textMuted: "#5d6861",
    textDisabled: "#b9c0ba",
    error: "#b3261e",
  },
  dark: {
    appBg: "#121513",
    surface: "#1b1f1c",
    surface2: "#202522",
    border: "#2e3530",
    borderSoft: "#262b27",
    borderStrong: "#3a423c",
    text: "#e6ebe7",
    textMuted: "#a2aca5",
    textDisabled: "#5f6962",
    error: "#f2b8b5",
  },
};

// The page's own stylesheets read these variables, so they follow the theme.
export function applyCssVariables(mode: "light" | "dark", accent: AccentKey) {
  const s = SURFACES[mode];
  const a = ACCENTS[accent][mode];
  const vars: Record<string, string> = {
    "--app-bg": s.appBg,
    "--surface": s.surface,
    "--surface-2": s.surface2,
    "--border": s.border,
    "--border-soft": s.borderSoft,
    "--border-strong": s.borderStrong,
    "--text": s.text,
    "--text-muted": s.textMuted,
    "--text-disabled": s.textDisabled,
    "--error": s.error,
    "--accent": a,
    "--accent-dark": mode === "dark" ? a : ACCENTS[accent].light,
    "--accent-soft": `${a}14`,
    "--on-accent": mode === "dark" ? "#101311" : "#ffffff",
  };
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.style.colorScheme = mode;
}

export function makeTheme(mode: "light" | "dark", accent: AccentKey) {
  const s = SURFACES[mode];
  const a = ACCENTS[accent][mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: a },
      secondary: { main: mode === "dark" ? "#d6a77a" : "#8a5a2b" },
      background: { default: s.appBg, paper: s.surface },
      text: { primary: s.text, secondary: s.textMuted },
      divider: s.border,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: { borderBottom: `1px solid ${s.border}`, background: s.surface },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: s.border },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": { backgroundColor: `${a}1f` },
            "&.Mui-selected:hover": { backgroundColor: `${a}2e` },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { backgroundColor: s.surface } },
      },
    },
  });
}
